import { useCallback, useEffect, useRef, useState } from 'react';
import { ASSESSMENT_PAGE_INDICATOR_SIZE } from '../constants';
import { AssessmentsPage } from '../types';

export const useAssessmentsDesktopPageNavigation = (pages: Array<AssessmentsPage>) => {
  const scrollRef = useRef<HTMLUListElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const checkScrollPosition = useCallback(() => {
    if (!scrollRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;

    // Show left arrow if we can scroll left (with a small threshold)
    setShowLeftArrow(scrollLeft > 5);

    // Show right arrow if we can scroll right (with a small threshold)
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 5);
  }, []);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return undefined;

    // Check initial scroll position
    checkScrollPosition();

    // Add scroll event listener
    scrollContainer.addEventListener('scroll', checkScrollPosition);

    // Add resize observer to handle container size changes
    const resizeObserver = new ResizeObserver(checkScrollPosition);
    resizeObserver.observe(scrollContainer);

    return () => {
      scrollContainer.removeEventListener('scroll', checkScrollPosition);
      resizeObserver.disconnect();
    };
  }, [pages, checkScrollPosition]);

  const scrollPages = useCallback((direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    // Guard against a container narrower than 2 indicators, which would otherwise yield a
    // zero/negative scroll amount and make the arrow appear unresponsive.
    const scrollAmount = Math.max(
      ASSESSMENT_PAGE_INDICATOR_SIZE,
      scrollRef.current.clientWidth - ASSESSMENT_PAGE_INDICATOR_SIZE * 2,
    );
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  }, []);

  const scrollLeft = useCallback(() => scrollPages('left'), [scrollPages]);

  const scrollRight = useCallback(() => scrollPages('right'), [scrollPages]);

  return {
    scrollRef,
    showLeftArrow,
    showRightArrow,
    scrollLeft,
    scrollRight,
  };
};
